import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  Typography,
} from "@mui/joy";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { subcontractorsApi } from "../../lib/apiSubcontractors";
import { apiClient } from "../../lib/api";
import { WorkOrderList, WorkOrderForm } from "../../components/subcontractors";
import { ResponsiveTable } from "../../components/common/ResponsiveTable";
import type { SubcontractorInvoice } from "../../types/subcontractors";
const money = (v: unknown) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(v || 0),
  );
interface Event {
  id: number;
  at: string;
  title: string;
  actor: string;
  reason?: string;
  filename?: string;
  changes: Record<string, { before: unknown; after: unknown }>;
}
const errorText = (e: unknown) => {
  const v = e as { responseData?: unknown; message?: string };
  return v.responseData
    ? JSON.stringify(v.responseData)
    : v.message || "Unable to save";
};
export const SubcontractorDetailPage: React.FC = () => {
  const { id } = useParams(),
    navigate = useNavigate(),
    client = useQueryClient();
  const [tab, setTab] = useState(0),
    [activityPage, setActivityPage] = useState(1),
    [reason, setReason] = useState(""),
    [expiry, setExpiry] = useState(""),
    [renewing, setRenewing] = useState(false),
    [creating, setCreating] = useState(false),
    [balanceOnly, setBalanceOnly] = useState(false);
  const profile = useQuery({
    queryKey: ["subcontractor", id],
    queryFn: () => subcontractorsApi.get(Number(id)),
    enabled: !!id,
  });
  const stats = useQuery({
    queryKey: ["subcontractor-statistics", id],
    queryFn: () => subcontractorsApi.getStatistics(Number(id)),
    enabled: !!id,
  });
  const invoices = useQuery({
    queryKey: ["subcontractor-invoices", id],
    queryFn: () => subcontractorsApi.getInvoices(Number(id)),
    enabled: !!id,
  });
  const history = useQuery({
    queryKey: ["subcontractor-activity", id, activityPage],
    queryFn: async () =>
      (
        await apiClient.get<{ results: Event[]; count: number }>(
          `/api/subcontractors/${id}/activity/?page=${activityPage}`,
        )
      ).data,
    enabled: !!id,
  });
  const refresh = () => {
    client.invalidateQueries({ queryKey: ["subcontractor", id] });
    client.invalidateQueries({ queryKey: ["subcontractor-activity", id] });
    client.invalidateQueries({ queryKey: ["subcontractors"] });
  };
  const upload = useMutation({
    mutationFn: async ({ kind, file }: { kind: string; file: File }) => {
      const data = new FormData();
      data.append("document_type", kind);
      data.append("file", file);
      return apiClient.post(`/api/subcontractors/${id}/upload-document/`, data);
    },
    onSuccess: refresh,
  });
  const renew = useMutation({
    mutationFn: () =>
      subcontractorsApi.update(Number(id), {
        insurance_info: {
          ...profile.data?.insurance_info,
          expiry_date: expiry,
        },
      }),
    onSuccess: () => {
      refresh();
      setRenewing(false);
    },
  });
  const reactivate = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/subcontractors/${id}/reactivate/`, { reason }),
    onSuccess: () => {
      refresh();
      setReason("");
    },
  });
  if (profile.isLoading)
    return <Typography sx={{ p: 3 }}>Loading subcontractor…</Typography>;
  if (profile.isError || !profile.data)
    return (
      <Alert color="danger">
        Unable to load subcontractor.{" "}
        <Button onClick={() => profile.refetch()}>Retry</Button>
      </Alert>
    );
  const sub = profile.data,
    metrics = stats.data,
    compliance = sub.compliance;
  const payable = (i: SubcontractorInvoice) =>
    ["pending", "approved", "partially_paid"].includes(i.status) &&
    Number(i.balance_due) > 0;
  const invoiceRows = (invoices.data || []).filter(
    (i) => !balanceOnly || payable(i),
  );
  return (
    <Stack
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 1400,
        mx: "auto",
        overflowWrap: "anywhere",
      }}
      spacing={3}
    >
      <Button
        variant="plain"
        sx={{ alignSelf: "flex-start" }}
        onClick={() => navigate("/operations/subcontractors")}
      >
        Back to subcontractors
      </Button>
      <Stack
        direction="row"
        sx={{ gap: 2, flexWrap: "wrap", justifyContent: "space-between" }}
      >
        <Box>
          <Typography level="h2">{sub.company_name}</Typography>
          <Typography>
            {sub.contact_person}
            {sub.dba_name && ` · DBA ${sub.dba_name}`}
          </Typography>
          <Chip sx={{ mt: 1 }}>{sub.status}</Chip>
        </Box>
        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={() => navigate(`/operations/subcontractors/${id}/edit`)}
          >
            Edit profile
          </Button>
          <Button variant="outlined" onClick={() => setCreating(true)}>
            Create work order
          </Button>
        </Stack>
      </Stack>
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
        {sub.phone && (
          <Button component="a" href={`tel:${sub.phone}`} variant="soft">
            Call {sub.phone}
          </Button>
        )}
        {sub.email && (
          <Button component="a" href={`mailto:${sub.email}`} variant="soft">
            Email
          </Button>
        )}
        {sub.address_1 && (
          <Button
            component="a"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sub.full_address || [sub.address_1, sub.city, sub.state].join(", "))}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="soft"
          >
            Directions
          </Button>
        )}
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Card sx={{ flex: 1 }}>
          <Typography level="title-lg">Business & qualifications</Typography>
          <Typography>
            {sub.specialties.map((s) => s.name).join(" · ") ||
              "No specialties listed"}
          </Typography>
          <Typography>
            {[sub.city, sub.state].filter(Boolean).join(", ")}
          </Typography>
          <Typography level="body-sm">
            {metrics?.active_work_orders ?? "—"} active orders · Confirm
            availability before assignment
          </Typography>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            {sub.notes || "No qualification notes yet."}
          </Typography>
          <Typography level="body-sm">
            License {sub.license_number || "not listed"} · Expires{" "}
            {sub.license_expiry || "not listed"}
          </Typography>
          <Typography level="body-sm">
            {sub.payment_terms.replaceAll("_", " ")} ·{" "}
            {sub.preferred_payment_method}
            {sub.custom_payment_terms && ` · ${sub.custom_payment_terms}`}
          </Typography>
        </Card>
        <Card sx={{ flex: 1 }}>
          <Typography level="title-lg">Performance</Typography>
          {stats.isError ? (
            <Alert color="danger">Unable to load statistics.</Alert>
          ) : (
            <>
              <Typography>
                {metrics?.completed_work_orders ?? "—"} completed orders
                (includes invoiced and paid)
              </Typography>
              <Typography>
                On time:{" "}
                {metrics?.on_time_completion_rate == null
                  ? "Not enough data"
                  : `${metrics.on_time_completion_rate.toFixed(0)}%`}{" "}
                · {metrics?.on_time_sample_count || 0} dated completions
              </Typography>
              <Typography>
                Quality:{" "}
                {metrics?.average_quality == null
                  ? "No feedback yet"
                  : `${metrics.average_quality.toFixed(1)}/5`}{" "}
                · {metrics?.quality_sample_count || 0} ratings
              </Typography>
              <Typography>
                Average completed value: {money(metrics?.average_job_value)}
              </Typography>
              <Button variant="plain" onClick={() => setTab(0)}>
                View work history
              </Button>
            </>
          )}
        </Card>
        <Card sx={{ flex: 1 }}>
          <Typography level="title-lg">Invoice balances</Typography>
          <Typography level="h3">
            {money(metrics?.outstanding_balance)}
          </Typography>
          <Typography level="body-sm">
            Remaining amounts on pending-review, approved and partially paid
            invoices. Disputed, rejected and paid invoices are excluded.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setBalanceOnly(true);
              setTab(1);
            }}
          >
            View contributing invoices
          </Button>
        </Card>
      </Stack>
      <Card>
        <Typography level="title-lg">
          Compliance: {compliance?.state || "Review needed"}
        </Typography>
        <Typography>
          Insurance expires {compliance?.insurance_expiry || "not recorded"}
        </Typography>
        {compliance?.missing?.length ? (
          <Alert color="warning">
            Missing: {compliance.missing.join(", ")}
          </Alert>
        ) : null}
        {compliance?.expired?.length ? (
          <Alert color="danger">Expired: {compliance.expired.join(", ")}</Alert>
        ) : null}
        {compliance?.expiring?.length ? (
          <Alert color="warning">
            Renew within 30 days: {compliance.expiring.join(", ")}
          </Alert>
        ) : null}
        <Typography level="body-sm">
          Renewing documents keeps a suspended profile suspended until someone
          reviews and reactivates it.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            setExpiry(sub.insurance_info?.expiry_date || "");
            setRenewing(true);
          }}
        >
          Update insurance expiry
        </Button>
        {sub.status !== "active" && (
          <Stack spacing={1}>
            <FormControl>
              <FormLabel>Compliance review notes</FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Document checks and approval reason"
              />
            </FormControl>
            <Button
              disabled={
                !reason.trim() ||
                !!compliance?.missing.length ||
                !!compliance?.expired.length
              }
              loading={reactivate.isPending}
              onClick={() => reactivate.mutate()}
            >
              Review and reactivate
            </Button>
          </Stack>
        )}
        {reactivate.isError && (
          <Alert color="danger">{errorText(reactivate.error)}</Alert>
        )}
      </Card>
      <Tabs value={tab} onChange={(_, v) => setTab(Number(v))}>
        <TabList sx={{ flexWrap: "wrap" }}>
          <Tab>Work history</Tab>
          <Tab>Invoices</Tab>
          <Tab>Documents</Tab>
          <Tab>Activity</Tab>
        </TabList>
        <TabPanel value={0}>
          <WorkOrderList subcontractorId={Number(id)} />
        </TabPanel>
        <TabPanel value={1}>
          <Stack spacing={2}>
            {balanceOnly && (
              <Button variant="plain" onClick={() => setBalanceOnly(false)}>
                Show all invoices
              </Button>
            )}
            {invoices.isError && (
              <Alert color="danger">Unable to load invoices.</Alert>
            )}
            <ResponsiveTable<SubcontractorInvoice>
              rows={invoiceRows}
              getRowKey={(i) => i.id}
              columns={[
                {
                  key: "number",
                  header: "Invoice",
                  mobileVariant: "header",
                  render: (i) => <Typography>{i.invoice_number}</Typography>,
                },
                {
                  key: "wo",
                  header: "Work order",
                  render: (i) => (
                    <Typography>{i.work_order_number || "—"}</Typography>
                  ),
                },
                {
                  key: "due",
                  header: "Due date",
                  render: (i) => <Typography>{i.due_date || "—"}</Typography>,
                },
                {
                  key: "total",
                  header: "Total",
                  render: (i) => (
                    <Typography>{money(i.total_amount)}</Typography>
                  ),
                },
                {
                  key: "balance",
                  header: "Remaining",
                  render: (i) => (
                    <Typography>{money(i.balance_due)}</Typography>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  render: (i) => <Chip>{i.status.replaceAll("_", " ")}</Chip>,
                },
              ]}
            />
          </Stack>
        </TabPanel>
        <TabPanel value={2}>
          <Stack spacing={2}>
            {["w9_document", "insurance_certificate"].map((kind) => {
              const doc = sub.documents?.[kind];
              return (
                <Card key={kind}>
                  <Typography level="title-md">
                    {kind === "w9_document" ? "W-9" : "Insurance certificate"}
                  </Typography>
                  <Typography>{doc?.filename || "No file uploaded"}</Typography>
                  <Typography level="body-sm">
                    {doc?.uploaded_at
                      ? `${doc.uploaded_by} · ${new Date(doc.uploaded_at).toLocaleString()}`
                      : doc?.url
                        ? "Existing file; uploader not recorded"
                        : "PDF, PNG or JPEG · Up to 10 MB"}
                  </Typography>
                  <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
                    {doc?.url && (
                      <Button
                        component="a"
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                      >
                        View document
                      </Button>
                    )}
                    <Button component="label" loading={upload.isPending}>
                      {doc?.url ? "Replace" : "Upload"}{" "}
                      {kind === "w9_document" ? "W-9" : "insurance"}
                      <input
                        aria-label={`Upload ${kind}`}
                        hidden
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) upload.mutate({ kind, file });
                          e.target.value = "";
                        }}
                      />
                    </Button>
                  </Stack>
                </Card>
              );
            })}
            {upload.isError && (
              <Alert color="danger">{errorText(upload.error)}</Alert>
            )}
          </Stack>
        </TabPanel>
        <TabPanel value={3}>
          <Stack spacing={2}>
            <Typography>
              Added {new Date(sub.date_added).toLocaleString()} by{" "}
              {sub.added_by?.first_name ||
                sub.added_by?.email ||
                "an unrecorded user"}
            </Typography>
            {history.isError && (
              <Alert color="danger">Unable to load activity.</Alert>
            )}
            {history.data?.results.map((e) => (
              <Card key={e.id}>
                <Typography level="title-md">{e.title}</Typography>
                <Typography level="body-sm">
                  {e.actor} · {new Date(e.at).toLocaleString()}
                </Typography>
                {e.reason && <Typography>{e.reason}</Typography>}
                {e.filename && <Typography>{e.filename}</Typography>}
                {Object.entries(e.changes || {}).map(([field, value]) => (
                  <Typography key={field} level="body-sm">
                    {field.replaceAll("_", " ")}: {JSON.stringify(value.before)}{" "}
                    → {JSON.stringify(value.after)}
                  </Typography>
                ))}
              </Card>
            ))}
            {!history.data?.results.length && (
              <Typography>No new activity recorded.</Typography>
            )}
            <Stack direction="row" spacing={1}>
              <Button
                disabled={activityPage === 1}
                onClick={() => setActivityPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Typography>Page {activityPage}</Typography>
              <Button
                disabled={activityPage * 20 >= (history.data?.count || 0)}
                onClick={() => setActivityPage((p) => p + 1)}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </TabPanel>
      </Tabs>
      <Modal open={renewing} onClose={() => setRenewing(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Insurance renewal</Typography>
          <FormControl>
            <FormLabel>Insurance expiry</FormLabel>
            <Input
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
            />
          </FormControl>
          {renew.isError && (
            <Alert color="danger">{errorText(renew.error)}</Alert>
          )}
          <Button onClick={() => renew.mutate()} loading={renew.isPending}>
            Save expiry
          </Button>
        </ModalDialog>
      </Modal>
      <Modal open={creating} onClose={() => setCreating(false)}>
        <ModalDialog
          sx={{
            width: "min(900px,95vw)",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <ModalClose />
          <Typography level="h4">
            Create work order for {sub.company_name}
          </Typography>
          <WorkOrderForm
            subcontractorId={Number(id)}
            onCancel={() => setCreating(false)}
            onSuccess={() => {
              setCreating(false);
              setTab(0);
              client.invalidateQueries({ queryKey: ["work-orders"] });
              client.invalidateQueries({
                queryKey: ["subcontractor-statistics", id],
              });
            }}
          />
        </ModalDialog>
      </Modal>
    </Stack>
  );
};
